import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react';

// El título de la pantalla actual, que se ve en el centro del app bar.
//
// **Lo pone cada página, no el app bar.** El app bar no sabe en qué pantalla
// está, y no debería: si dedujera el título de la ruta, cada pantalla nueva
// obligaría a tocarlo, y `/sucursales/:id` no sabría decir «Editar» o «Nueva».

interface Contexto {
  titulo: string;
  ponerTitulo: (titulo: string) => void;
}

const ContextoTitulo = createContext<Contexto | null>(null);

export function ProveedorTitulo({ children }: { children: ReactNode }) {
  const [titulo, ponerTitulo] = useState('');
  const valor = useMemo(() => ({ titulo, ponerTitulo }), [titulo]);
  return <ContextoTitulo value={valor}>{children}</ContextoTitulo>;
}

function useContextoTitulo(): Contexto {
  const contexto = use(ContextoTitulo);
  if (!contexto) {
    throw new Error('El título de pantalla necesita estar dentro de <ProveedorTitulo>.');
  }
  return contexto;
}

/**
 * Lo que llama cada página para decir cómo se llama:
 *
 *   useTituloPantalla(t('admin.sucursales.titulo'));
 *
 * Al desmontarse lo borra. Si no, una pantalla que no lo pone —la guarda sin
 * permiso, por ejemplo— mostraría el título de la anterior.
 */
export function useTituloPantalla(titulo: string) {
  const { ponerTitulo } = useContextoTitulo();
  useEffect(() => {
    ponerTitulo(titulo);
    return () => ponerTitulo('');
  }, [titulo, ponerTitulo]);
}

/** El título vigente. Solo lo lee el app bar. */
export function useTituloActual(): string {
  return useContextoTitulo().titulo;
}
